import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Play,
  Zap,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
interface ProcessingRun {
  id: string;
  emailId?: string;
  classificationId?: string;
  orderId?: string;
  runType: string;
  runStatus: string;
  triggeredBy?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  parserUsed?: string;
  itemsProcessed: number;
  itemsSuccessful: number;
  itemsFailed: number;
  errorMessage?: string;
  errorDetails?: Record<string, unknown>;
  retryCount: number;
  createdAt: string;
}

interface ProcessingStep {
  id: string;
  runId: string;
  stepNumber: number;
  stepName: string;
  stepType: string;
  stepStatus: string;
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  message?: string;
  errorDetails?: Record<string, unknown>;
}

// Status configuration
const statusConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string; borderColor: string }> = {
  pending: { icon: Clock, color: 'text-gray-500', bgColor: 'bg-gray-100', borderColor: 'border-gray-300' },
  running: { icon: RefreshCw, color: 'text-blue-500', bgColor: 'bg-blue-100', borderColor: 'border-blue-400' },
  success: { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-100', borderColor: 'border-green-400' },
  warning: { icon: AlertTriangle, color: 'text-yellow-500', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-400' },
  error: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100', borderColor: 'border-red-400' },
  skipped: { icon: ChevronRight, color: 'text-gray-400', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
  completed: { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-100', borderColor: 'border-green-400' },
  failed: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100', borderColor: 'border-red-400' },
};

// JSON Viewer Component
function JsonViewer({ data, title }: { data: Record<string, unknown> | undefined; title: string }) {
  const [copied, setCopied] = useState(false);

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-500 italic">No {title.toLowerCase()} data</p>
      </div>
    );
  }

  const jsonString = JSON.stringify(data, null, 2);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase">{title}</span>
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto text-xs leading-relaxed max-h-80 overflow-y-auto">
        {jsonString}
      </pre>
    </div>
  );
}

// Step Node Component (like n8n node)
function StepNode({
  step,
  isExpanded,
  onToggle,
  isLast,
}: {
  step: ProcessingStep;
  isExpanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}) {
  const status = statusConfig[step.stepStatus] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="relative">
      {/* Connection line */}
      {!isLast && (
        <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gray-200" style={{ height: isExpanded ? 'auto' : '40px' }} />
      )}

      {/* Step card */}
      <div
        className={clsx(
          'relative bg-white rounded-xl border-2 transition-all cursor-pointer hover:shadow-md',
          status.borderColor,
          isExpanded && 'shadow-lg'
        )}
        onClick={onToggle}
      >
        {/* Header */}
        <div className="flex items-center gap-4 p-4">
          <div className={clsx('p-2 rounded-lg', status.bgColor)}>
            <StatusIcon
              className={clsx('w-5 h-5', status.color, step.stepStatus === 'running' && 'animate-spin')}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Step {step.stepNumber}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{step.stepType}</span>
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{step.stepName}</h3>
            {step.message && (
              <p className="text-sm text-gray-500 truncate mt-0.5">{step.message}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            {step.durationMs !== undefined && (
              <span className="text-sm text-gray-500">{step.durationMs}ms</span>
            )}
            <ChevronDown
              className={clsx('w-5 h-5 text-gray-400 transition-transform', isExpanded && 'rotate-180')}
            />
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            {/* Timing info */}
            <div className="flex flex-wrap gap-4 text-sm">
              {step.startedAt && (
                <div>
                  <span className="text-gray-500">Started: </span>
                  <span className="text-gray-900">{format(new Date(step.startedAt), 'HH:mm:ss.SSS')}</span>
                </div>
              )}
              {step.completedAt && (
                <div>
                  <span className="text-gray-500">Completed: </span>
                  <span className="text-gray-900">{format(new Date(step.completedAt), 'HH:mm:ss.SSS')}</span>
                </div>
              )}
              {step.durationMs !== undefined && (
                <div>
                  <span className="text-gray-500">Duration: </span>
                  <span className="font-medium text-gray-900">{step.durationMs}ms</span>
                </div>
              )}
            </div>

            {/* Error details */}
            {step.errorDetails && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800 mb-2">Error Details</p>
                <pre className="text-xs text-red-700 overflow-x-auto">
                  {JSON.stringify(step.errorDetails, null, 2)}
                </pre>
              </div>
            )}

            {/* Input/Output JSON */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <JsonViewer data={step.inputData} title="Input Data" />
              <JsonViewer data={step.outputData} title="Output Data" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProcessingRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Fetch run with steps
  const { data, isLoading, error, refetch } = useQuery<{ data: { run: ProcessingRun; steps: ProcessingStep[] } }>({
    queryKey: ['processing-run', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/processing/runs/${id}`);
      if (!res.ok) throw new Error('Failed to fetch run');
      return res.json();
    },
    refetchInterval: (data) => {
      // Auto-refresh while running
      if (data?.data?.run?.runStatus === 'running') return 2000;
      return false;
    },
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/processing/runs/${id}/retry`, { method: 'POST' });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || 'Retry failed');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['processing-runs'] });
      // Navigate to new run
      if (data?.data?.id) {
        navigate(`/processing/${data.data.id}`);
      }
    },
  });

  const toggleStep = (stepNumber: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!data?.data?.steps) return;
    setExpandedSteps(new Set(data.data.steps.map((s) => s.stepNumber)));
  };

  const collapseAll = () => {
    setExpandedSteps(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-perfectted-primary" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Failed to load run</h2>
        <p className="text-gray-500 mt-2">{error?.message || 'Run not found'}</p>
        <Link to="/processing" className="text-perfectted-primary hover:text-perfectted-dark mt-4 inline-block">
          Back to Processing History
        </Link>
      </div>
    );
  }

  const { run, steps } = data.data;
  const runStatus = statusConfig[run.runStatus] || statusConfig.pending;
  const RunStatusIcon = runStatus.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/processing')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Processing Run</h1>
              <div className={clsx('flex items-center gap-2 px-3 py-1 rounded-full', runStatus.bgColor)}>
                <RunStatusIcon
                  className={clsx('w-4 h-4', runStatus.color, run.runStatus === 'running' && 'animate-spin')}
                />
                <span className={clsx('text-sm font-medium capitalize', runStatus.color)}>
                  {run.runStatus}
                </span>
              </div>
            </div>
            <p className="text-gray-500 mt-1 font-mono text-sm">{run.id}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {run.runStatus === 'failed' && (
            <button
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              <RefreshCw className={clsx('w-4 h-4', retryMutation.isPending && 'animate-spin')} />
              {retryMutation.isPending ? 'Retrying...' : 'Retry'}
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Run Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Type</p>
          <p className="font-semibold text-gray-900 capitalize">{run.runType.replace('_', ' ')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Triggered By</p>
          <p className="font-semibold text-gray-900 capitalize">{run.triggeredBy || '-'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Parser</p>
          <p className="font-semibold text-gray-900">{run.parserUsed || '-'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Duration</p>
          <p className="font-semibold text-gray-900">{run.durationMs ? `${run.durationMs}ms` : '-'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Items</p>
          <p className="font-semibold">
            <span className="text-green-600">{run.itemsSuccessful}</span>
            {run.itemsFailed > 0 && (
              <span className="text-red-600"> / {run.itemsFailed} failed</span>
            )}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Started</p>
          <p className="font-semibold text-gray-900 text-sm">
            {run.startedAt ? format(new Date(run.startedAt), 'HH:mm:ss') : '-'}
          </p>
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-wrap gap-4">
        {run.orderId && (
          <Link
            to={`/orders/${run.orderId}`}
            className="flex items-center gap-2 text-sm text-perfectted-primary hover:text-perfectted-dark"
          >
            <ExternalLink className="w-4 h-4" />
            View Order
          </Link>
        )}
        {run.retryCount > 0 && (
          <span className="flex items-center gap-2 text-sm text-orange-600">
            <RefreshCw className="w-4 h-4" />
            Retry #{run.retryCount}
          </span>
        )}
      </div>

      {/* Error Message */}
      {run.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800">Error</h4>
              <p className="text-red-700 mt-1">{run.errorMessage}</p>
              {run.errorDetails && (
                <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-x-auto">
                  {JSON.stringify(run.errorDetails, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Steps Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-perfectted-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Processing Steps</h2>
            <span className="px-2 py-0.5 bg-gray-100 rounded text-sm text-gray-600">
              {steps.length} steps
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Expand All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAll}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Collapse All
            </button>
          </div>
        </div>

        {steps.length === 0 ? (
          <div className="text-center py-8">
            <Play className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No steps recorded yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {steps.map((step, index) => (
              <StepNode
                key={step.id}
                step={step}
                isExpanded={expandedSteps.has(step.stepNumber)}
                onToggle={() => toggleStep(step.stepNumber)}
                isLast={index === steps.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
